import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import {
  GroupTransportSelectInput,
  useTransportGroupData,
} from "components/Common/GroupTransportSelect";
import JsonEditor from "components/Common/JsonEditor";
import { SHIPPING_TYPE } from "constants/order";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";
import MessageFormValidate from "../../components/shared-components/MessageFormValidate";
import {
  formConvertErrorMessage,
  formValidate,
} from "../../helpers/form_helper";
import {
  useCreateTransport,
  useListShippingType,
  useUpdateTransport,
} from "../../hooks";
import { REF_CODE_PATTERN, getLabelShipingTypeByValue } from "./utils";

const TransportForm = ({
  isOpen,
  transport,
  // is_level_2,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation(["transport"]);
  const { getGroupTransportOption, optionsSelect } = useTransportGroupData();
  // const [addressLevel, setAddressLevel] = useState(is_level_2);

  const initDefaultValues = {
    hub_id: transport?.hub
      ? {
          label: transport?.hub?.name,
          value: parseInt(transport?.hub?.id),
        }
      : undefined,
    code: transport?.code || "",
    name: transport?.name || "",
    shipping_type: transport?.shipping_type
      ? {
          label: getLabelShipingTypeByValue(t, transport?.shipping_type),
          value: transport?.shipping_type,
        }
      : undefined,
    group_code: getGroupTransportOption(transport?.group_code, optionsSelect),
    allowed_select_action_date: Boolean(
      transport?.allowed_select_action_date || 0
    ),
    tracking_url: transport?.tracking_url || "",
  };
  const [jsonInput, setJsonInput] = useState(undefined);

  const { data: listShippingType, refetch: fetchListShippingType } =
    useListShippingType({
      params: {
        page: -1,
      },
    });

  useEffect(() => {
    if (transport && transport?.cfg) {
      setJsonInput(JSON.stringify(transport.cfg));
    }
  }, [transport]);

  useEffect(() => {
    if (isOpen) {
      fetchListShippingType();
    }
  }, [isOpen]);

  // useEffect(() => {
  //   if (!transport) {
  //     setAddressLevel(is_level_2);
  //   }
  // }, [is_level_2, transport]);

  // useEffect(() => {
  //   if (transport && transport.hub?.is_new_address) {
  //     const hubIsLevel2 = transport.hub.is_new_address === "N";
  //     setAddressLevel(hubIsLevel2);
  //   }
  // }, [transport]);

  const onTransportSuccess = data => {
    onSuccess?.(data.data);
  };
  const { addToast } = useToasts();
  const onErrorTransport = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { mutate: createTransport } = useCreateTransport(
    onTransportSuccess,
    onErrorTransport
  );
  const { mutate: updateTransport } = useUpdateTransport(
    onTransportSuccess,
    onErrorTransport
  );
  const validator = {
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.hub_id") })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(255, t("form.validate.char_max", { max: 255 })),
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") }))
      .max(20, t("form.validate.char_max", { max: 20 })),
    shipping_type: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.type") })),
    group_code: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("group_transport") })),
    tracking_url: yup.string().test({
      name: "test",
      message: t("form.validate.tracking_url_message"),
      test: value => value && value.includes(REF_CODE_PATTERN),
    }),
  };
  const transportValidationSchema = yup.object().shape(validator);
  const mainForm = useForm({
    resolver: yupResolver(transportValidationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const {
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    setError,
    register,
    trigger,
    control,
    watch,
    setValue,
  } = mainForm;

  const shippingTypeWatch = watch("shipping_type");

  // const handleChangeAddressLevel = newAddressLevel => {
  //   if (!transport) {
  //     setAddressLevel(newAddressLevel);
  //     setValue("hub_id", null);
  //   }
  // };

  const handleSubmitForm = () => {
    trigger();
    handleSubmit(() => {
      const data = getValues();

      if (transport) {
        const dataUpdate = {
          ...data,
          transportId: transport.id,
          hub_id: data?.hub_id?.value,
          cfg: jsonInput ? jsonInput : null,
          shipping_type: data?.shipping_type?.value,
          group_code: data?.group_code?.value,
          allowed_select_action_date: data?.allowed_select_action_date ? 1 : 0,
          tracking_url: data?.tracking_url || "",
        };
        updateTransport(dataUpdate);
      } else {
        const dataCreate = {
          hub_id: data?.hub_id?.value,
          name: data?.name,
          code: data?.code,
          cfg: jsonInput ? jsonInput : undefined,
          shipping_type: data?.shipping_type?.value,
          group_code: data?.group_code?.value,
          allowed_select_action_date: data?.allowed_select_action_date ? 1 : 0,
          tracking_url: data?.tracking_url,
        };
        createTransport(dataCreate);
      }
    })();
  };

  const handleJsonEditChange = value => {
    setJsonInput(value);
  };

  const isDisableAllowChoosenActionDate = useMemo(() => {
    if (shippingTypeWatch) {
      if (shippingTypeWatch.value === SHIPPING_TYPE.DVVC) {
        return false;
      } else {
        setValue("allowed_select_action_date", true);
        return true;
      }
    }

    return true;
  }, [shippingTypeWatch]);

  return (
    <>
      <Form className="pt-4 px-4" onSubmit={handleSubmitForm}>
        <Row>
          {/* {!transport && (
            <Col xs={12}>
              <FormGroup>
                <Label
                  className="d-flex align-items-center gap-1"
                  style={{ cursor: "pointer" }}
                  htmlFor="is_level_2"
                  onClick={() => handleChangeAddressLevel(!addressLevel)}
                >
                  <Switch
                    checked={addressLevel}
                    onChange={handleChangeAddressLevel}
                  />
                  Địa chỉ 2 cấp
                </Label>
              </FormGroup>
            </Col>
          )} */}
          <Col xs={6}>
            <FormHubSelect
              isMyHubOnly
              isRequired
              mainForm={mainForm}
              fieldName="hub_id"
              t={t}
              title={t("form.hub_id")}
              isDisabled={transport}
              // addressLevel={addressLevel ? 2 : 3}
              // key={`hub-select-${addressLevel}-${transport?.id || "create"}`} // Force re-render when address level changes or between create/update
            />
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="code">
                Code <span className="text-danger">*</span>
              </Label>
              <input
                name="code"
                type="text"
                className={`form-control ${formValidate(
                  errors,
                  touchedFields,
                  "code"
                )}`}
                placeholder={t("placeholder.enter", {
                  title: t("form.code"),
                })}
                disabled={transport}
                {...register("code")}
              />
              {errors.code && (
                <MessageFormValidate error={errors.code.message} />
              )}
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="name">
                {t("form.name")} <span className="text-danger">*</span>
              </Label>
              <input
                name="code"
                type="text"
                className={`form-control ${formValidate(
                  errors,
                  touchedFields,
                  "name"
                )}`}
                placeholder={t("placeholder.enter", {
                  title: t("form.name"),
                })}
                disabled={transport}
                {...register("name")}
              />
              {errors.name && (
                <MessageFormValidate error={errors.name.message} />
              )}
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label htmlFor="shipping_type">
                {t("form.type")} <span className="text-danger">*</span>
              </Label>
              <Controller
                control={control}
                name="shipping_type"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("form.type"),
                    })}
                    options={
                      listShippingType.map(item => ({
                        label: item.name,
                        value: item.code,
                      })) || []
                    }
                    isSearchable
                    className="basic-single w-100"
                    classNamePrefix="select"
                  />
                )}
              />
              {errors.shipping_type && (
                <MessageFormValidate error={errors.shipping_type.message} />
              )}
            </FormGroup>
          </Col>
          <Col xs={6}>
            <GroupTransportSelectInput
              fieldName={"group_code"}
              mainForm={mainForm}
              required={true}
            />
          </Col>
          <Col xs={6} className="d-flex align-items-center">
            <FormGroup
              className="d-flex align-items-center mx-2 mt-4"
              style={{ marginBottom: 0 }}
            >
              <input
                id="allowed_select_action_date"
                type="checkbox"
                disabled={isDisableAllowChoosenActionDate}
                {...register("allowed_select_action_date")}
              />
              <Label
                style={{
                  fontWeight: 400,
                  marginLeft: "16px",
                  marginBottom: 0,
                }}
                htmlFor="allowed_select_action_date"
              >
                {t("form.allowed_select_action_date")}
              </Label>
            </FormGroup>
          </Col>
          <Col xs={12}>
            <FormGroup>
              <Label htmlFor="tracking_url">Tracking URL Format</Label> <br />
              <p>
                Lưu ý: URL phải có chuỗi {REF_CODE_PATTERN}, ref_code sẽ được
                thay đổi tương ứng với đơn hàng
              </p>
              <input
                name="tracking_url"
                type="text"
                className={`form-control ${formValidate(
                  errors,
                  touchedFields,
                  "tracking_url"
                )}`}
                placeholder={t("placeholder.enter", {
                  title: "Tracking URL Format",
                })}
                {...register("tracking_url")}
              />
              {errors.tracking_url && (
                <MessageFormValidate error={errors.tracking_url.message} />
              )}
            </FormGroup>
          </Col>
        </Row>
      </Form>
      <Row className="mx-4">
        <Col>
          <FormGroup>
            <Label htmlFor="name">{t("form.json_input")}</Label>
          </FormGroup>
        </Col>
        <JsonEditor stateValue={jsonInput} onChange={handleJsonEditChange} />
      </Row>
      <Row>
        <Col className="text-end m-4">
          <Button
            size="xs"
            color="primary"
            style={{ backgroundColor: "#3C64B1" }}
            onClick={handleSubmitForm}
          >
            {transport ? t("button.update") : t("button.add")}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default TransportForm;
